import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { normalizePost } from '../../services/postService';
import { Avatar } from '../ui';
import PremiumName from '../ui/PremiumName';
import { useUserData } from '../../hooks/useUserData';
import { formatPostDate } from '../../utils/dateFormatter';
import { DEFAULT_IMAGES } from '../../constants/defaults';
import { useAuth } from '../../hooks/useAuth';

const RecentActivityAuthor = ({ post }) => {
  const { userData: currentUserData } = useAuth();
  const isOwnPost = post?.userId === currentUserData?.id;
  const { userData: authorData } = useUserData(isOwnPost ? null : post?.userId);
  const liveData = isOwnPost ? currentUserData : authorData;
  const authorName = post?.authorInfo?.displayName || post?.userName || liveData?.displayName || 'Usuario';
  const isPremium = liveData?.isPremium ?? post?.authorInfo?.isPremium ?? post?.isPremiumAuthor ?? false;
  const premiumColor = liveData?.premiumNameColor ?? post?.authorInfo?.premiumNameColor ?? post?.premiumNameColor ?? null;
  return (
    <PremiumName
      name={authorName}
      isPremium={isPremium}
      premiumColor={premiumColor}
    />
  );
};

const RecentActivityAvatar = ({ post }) => {
  const { userData: currentUserData } = useAuth();
  const isOwnPost = post?.userId === currentUserData?.id;
  const { userData: authorData } = useUserData(isOwnPost ? null : post?.userId);
  const liveData = isOwnPost ? currentUserData : authorData;
  const authorName = post?.authorInfo?.displayName || post?.userName || 'Usuario';
  const authorPhotoURL = isOwnPost
    ? (liveData?.photoURL ?? null)
    : (post?.authorInfo?.profileImage || post?.userPhotoURL || liveData?.photoURL || null);
  return (
    <Avatar
      src={authorPhotoURL}
      alt={authorName}
      fallbackText={authorName}
      size="md"
    />
  );
};

/**
 * RecentActivity - Timeline de actividad de usuarios que sigues
 * Muestra posts recientes de quienes sigues
 */
const RecentActivity = ({ selectedCategory = 'all' }) => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userData?.following && userData.following.length > 0) {
      loadRecentActivity();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.following]);

  const loadRecentActivity = async () => {
    try {
      const following = userData?.following || [];
      if (following.length === 0) {
        setLoading(false);
        return;
      }

      // Firestore limit: max 10 items in 'in' query
      const followingChunk = following.slice(0, 10);
      
      const postsRef = collection(db, 'posts');
      const q = query(
        postsRef,
        where('userId', 'in', followingChunk),
        orderBy('createdAt', 'desc'),
        limit(5)
      );

      const snapshot = await getDocs(q);
      const posts = snapshot.docs.map(doc => normalizePost({ id: doc.id, ...doc.data() }));

      setActivities(posts);
    } catch (error) {
      console.error('Error al cargar actividad reciente:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
          Actividad reciente
        </h2>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-start gap-3 p-4 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 animate-pulse">
              <div className="w-10 h-10 bg-gray-200 dark:bg-slate-700 rounded-full" />
              <div className="flex-1">
                <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-3/4 mb-2" />
                <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const filteredActivities =
    selectedCategory === 'all'
      ? activities
      : activities.filter((p) => p?.category === selectedCategory);

  if (filteredActivities.length === 0) return null;

  return (
    <div className="mb-8">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        Actividad reciente
      </h2>
      <div className="space-y-3">
        {filteredActivities.map((post) => {
          const firstMedia = post.media && post.media.length > 0 ? post.media[0] : null;

          return (
            <div
              key={post.id}
              onClick={() => navigate(`/post/${post.id}`)}
              className="flex items-start gap-3 p-4 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 hover:shadow-md transition-all cursor-pointer group"
            >
              <Link
                to={`/profile/${post.userId}`}
                onClick={(e) => e.stopPropagation()}
                className="flex-shrink-0"
              >
                <RecentActivityAvatar post={post} />
              </Link>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Link
                    to={`/profile/${post.userId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="font-medium text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    <RecentActivityAuthor post={post} />
                  </Link>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    publico un post
                  </span>
                  {post.createdAt && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      • {formatPostDate(post.createdAt)}
                    </span>
                  )}
                </div>

                {post.caption && (
                  <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 mb-2">
                    {post.caption}
                  </p>
                )}

                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                    </svg>
                    <span>{post.likesCount || 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span>{post.commentsCount || 0}</span>
                  </div>
                </div>
              </div>

              {firstMedia && (
                <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden relative">
                  {firstMedia.type === 'video' ? (
                    <>
                      <video
                        src={firstMedia.url}
                        poster={firstMedia.thumbnailUrl || DEFAULT_IMAGES.VIDEO_POSTER}
                        muted
                        playsInline
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        preload="metadata"
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <svg className="w-5 h-5 text-white/90" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </>
                  ) : (
                    <img
                      src={firstMedia.url}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecentActivity;
